// Substitution Management — Assignment API
// Covers: getDraftAssignments, updateAssignment, removeAssignment,
//         postPlan, recallAndRedraft, getPostedAssignmentsForDate

/// <reference lib="dom" />

import { handleApiCall, ApiError } from './errors'
import { getAccessToken } from './auth-token'
import { writeAuditEntry } from './substitution-audit-api'
import type { DraftAssignment, PostResult, Assignment } from '@timetable/types'

const SUPABASE_URL = (import.meta as any).env?.VITE_SUPABASE_URL as string
const SUPABASE_ANON_KEY = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY as string

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error('Missing Supabase environment variables')
}

function authHeaders(accessToken: string): Record<string, string> {
  return {
    apikey: SUPABASE_ANON_KEY,
    Authorization: `Bearer ${accessToken}`,
    'Content-Type': 'application/json',
  }
}

// ============================================================================
// getDraftAssignments
// ============================================================================

/**
 * Return all assignments for a given absence+date with joined relations.
 * Includes both draft and posted assignments so the panel can show the full picture.
 * Requirements: 4.1, 5.1, 5.3, 5.4, 5.5
 */
export async function getDraftAssignments(
  absenceId: string,
  date: string
): Promise<DraftAssignment[]> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const headers = authHeaders(accessToken)

    const params = new URLSearchParams()
    params.append(
      'select',
      [
        '*',
        'class_section:class_sections(*,class:classes(*,class_schedule:class_schedules(*)))',
        'period:schedule_periods(*)',
        'subject:subjects(*)',
        'substitute_teacher:teachers!assignments_substitute_teacher_id_fkey(*)',
        'absent_teacher:teachers!assignments_absent_teacher_id_fkey(*)',
      ].join(',')
    )
    params.append('absence_id', `eq.${absenceId}`)
    params.append('date', `eq.${date}`)
    params.append('status', `in.(draft,posted)`)
    params.append('order', 'period_id.asc')

    const response = await fetch(`${SUPABASE_URL}/rest/v1/assignments?${params}`, { headers })

    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      throw new Error(err.message || 'Failed to fetch draft assignments')
    }

    const assignments: any[] = await response.json()

    // Compute substitute_daily_count for each assigned substitute
    const substituteDailyCounts = new Map<string, number>()
    const subIds = [
      ...new Set(
        assignments
          .map((a) => a.substitute_teacher_id)
          .filter(Boolean) as string[]
      ),
    ]

    if (subIds.length > 0) {
      const countParams = new URLSearchParams()
      countParams.append('select', 'substitute_teacher_id')
      countParams.append('date', `eq.${date}`)
      countParams.append('status', `in.(posted,draft)`)
      countParams.append('substitute_teacher_id', `in.(${subIds.join(',')})`)

      const countResponse = await fetch(
        `${SUPABASE_URL}/rest/v1/assignments?${countParams}`,
        { headers }
      )

      if (countResponse.ok) {
        const rows: { substitute_teacher_id: string }[] = await countResponse.json()
        for (const row of rows) {
          const tid = row.substitute_teacher_id
          substituteDailyCounts.set(tid, (substituteDailyCounts.get(tid) ?? 0) + 1)
        }
      }
    }

    return assignments.map((a) => ({
      ...a,
      substitute_daily_count: a.substitute_teacher_id
        ? (substituteDailyCounts.get(a.substitute_teacher_id) ?? 0)
        : undefined,
    })) as DraftAssignment[]
  })
}

// ============================================================================
// updateAssignment
// ============================================================================

/**
 * Update the substitute teacher for a draft assignment and mark it as manually overridden.
 * Writes an `assignment_manual_override` audit log entry.
 * Requirements: 5.1, 5.3, 5.4
 */
export async function updateAssignment(
  id: string,
  substituteTeacherId: string
): Promise<Assignment> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const headers = authHeaders(accessToken)

    // Fetch current assignment to get context for audit log
    const fetchParams = new URLSearchParams()
    fetchParams.append('select', 'id,absence_id,date,substitute_teacher_id,school_id:absences(school_id)')
    fetchParams.append('id', `eq.${id}`)

    const fetchResponse = await fetch(`${SUPABASE_URL}/rest/v1/assignments?${fetchParams}`, {
      headers,
    })

    let absenceId = ''
    let targetDate = ''
    let schoolId = ''
    let oldSubstituteId: string | null = null

    if (fetchResponse.ok) {
      const rows: any[] = await fetchResponse.json()
      if (rows.length > 0) {
        absenceId = rows[0].absence_id
        targetDate = rows[0].date
        oldSubstituteId = rows[0].substitute_teacher_id
        schoolId = rows[0].school_id?.school_id ?? ''
      }
    }

    // Update the assignment
    const patchResponse = await fetch(`${SUPABASE_URL}/rest/v1/assignments?id=eq.${id}`, {
      method: 'PATCH',
      headers: {
        ...headers,
        Prefer: 'return=representation',
      },
      body: JSON.stringify({
        substitute_teacher_id: substituteTeacherId,
        assignment_method: 'manual',
      }),
    })

    if (!patchResponse.ok) {
      const err = await patchResponse.json().catch(() => ({}))
      throw new Error(err.message || 'Failed to update assignment')
    }

    const data: any[] = await patchResponse.json()
    if (!data || data.length === 0) throw new Error('Assignment not found')

    // Write audit log entry
    await writeAuditEntry({
      school_id: schoolId,
      absence_id: absenceId,
      assignment_id: id,
      action: 'assignment_manual_override',
      target_date: targetDate,
      details: {
        old_substitute_teacher_id: oldSubstituteId,
        new_substitute_teacher_id: substituteTeacherId,
      },
    })

    return data[0] as Assignment
  })
}

// ============================================================================
// removeAssignment
// ============================================================================

/**
 * Delete a draft assignment record.
 * Writes an `assignment_removed` audit log entry.
 * Requirements: 5.5
 */
export async function removeAssignment(id: string): Promise<void> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const headers = authHeaders(accessToken)

    // Fetch assignment context before deleting
    const fetchParams = new URLSearchParams()
    fetchParams.append('select', 'id,absence_id,date,substitute_teacher_id,period_id')
    fetchParams.append('id', `eq.${id}`)

    const fetchResponse = await fetch(`${SUPABASE_URL}/rest/v1/assignments?${fetchParams}`, {
      headers,
    })

    let absenceId = ''
    let targetDate = ''
    let schoolId = ''

    if (fetchResponse.ok) {
      const rows: any[] = await fetchResponse.json()
      if (rows.length > 0) {
        absenceId = rows[0].absence_id
        targetDate = rows[0].date

        // Fetch school_id from absence
        const absParams = new URLSearchParams()
        absParams.append('select', 'school_id')
        absParams.append('id', `eq.${absenceId}`)
        const absResponse = await fetch(`${SUPABASE_URL}/rest/v1/absences?${absParams}`, {
          headers,
        })
        if (absResponse.ok) {
          const absRows: any[] = await absResponse.json()
          schoolId = absRows[0]?.school_id ?? ''
        }
      }
    }

    // Delete the assignment
    const deleteResponse = await fetch(`${SUPABASE_URL}/rest/v1/assignments?id=eq.${id}`, {
      method: 'DELETE',
      headers,
    })

    if (!deleteResponse.ok) {
      const err = await deleteResponse.json().catch(() => ({}))
      throw new Error(err.message || 'Failed to remove assignment')
    }

    // Write audit log entry
    await writeAuditEntry({
      school_id: schoolId,
      absence_id: absenceId,
      assignment_id: id,
      action: 'assignment_removed',
      target_date: targetDate,
      details: { assignment_id: id },
    })
  })
}

// ============================================================================
// postPlan
// ============================================================================

/**
 * Post all draft assignments for a given absence+date.
 *
 * - Uses a conditional UPDATE that only succeeds when assignments are still `draft`
 *   (concurrent post guard — Req 21.10, 21.11).
 * - Checks sender email before posting when notification channel includes email.
 * - Triggers notifications for all posted assignments.
 * - Writes a `plan_posted` audit log entry.
 * - The operation is as atomic as possible: if the UPDATE finds no draft rows,
 *   it returns early without triggering notifications.
 *
 * Requirements: 6.1, 6.2, 6.3, 6.5, 6.6, 6.7, 6.8, 6.9, 21.10, 21.11
 */
export async function postPlan(absenceId: string, date: string): Promise<PostResult> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const headers = authHeaders(accessToken)

    // Fetch absence to get school_id
    const absenceParams = new URLSearchParams()
    absenceParams.append('select', 'school_id,teacher_id')
    absenceParams.append('id', `eq.${absenceId}`)

    const absenceResponse = await fetch(`${SUPABASE_URL}/rest/v1/absences?${absenceParams}`, {
      headers,
    })

    if (!absenceResponse.ok) {
      const err = await absenceResponse.json().catch(() => ({}))
      throw new Error(err.message || 'Failed to fetch absence')
    }

    const absences: any[] = await absenceResponse.json()
    if (!absences || absences.length === 0) throw new Error('Absence not found')
    const absence = absences[0]
    const schoolId: string = absence.school_id

    // Check sender email (Req 6.2) — removed: emails always go via the system Gmail account

    // Conditional UPDATE: only update rows that are still `draft`
    // This is the concurrent post guard — if another admin already posted,
    // the WHERE clause matches 0 rows and we return early.
    const patchParams = new URLSearchParams()
    patchParams.append('absence_id', `eq.${absenceId}`)
    patchParams.append('date', `eq.${date}`)
    patchParams.append('status', `eq.draft`) // only update drafts

    const patchResponse = await fetch(
      `${SUPABASE_URL}/rest/v1/assignments?${patchParams}`,
      {
        method: 'PATCH',
        headers: {
          ...headers,
          Prefer: 'return=representation',
        },
        body: JSON.stringify({ status: 'posted' }),
      }
    )

    if (!patchResponse.ok) {
      const err = await patchResponse.json().catch(() => ({}))
      throw new Error(err.message || 'Failed to post plan')
    }

    const postedAssignments: any[] = await patchResponse.json()

    // Concurrent post guard: if no rows were updated, the plan was already posted
    if (postedAssignments.length === 0) {
      // Return success without triggering duplicate notifications
      return { posted_count: 0, notification_triggered: false }
    }

    // Trigger notifications for all posted assignments that have a substitute
    const assignmentIdsWithSub = postedAssignments
      .filter((a) => a.substitute_teacher_id !== null)
      .map((a) => a.id)

    let notificationTriggered = false
    if (assignmentIdsWithSub.length > 0) {
      try {
        const notifResponse = await fetch(
          `${SUPABASE_URL}/functions/v1/send-notification`,
          {
            method: 'POST',
            headers: {
              apikey: SUPABASE_ANON_KEY,
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              assignmentIds: assignmentIdsWithSub,
              type: 'assignment',
            }),
          }
        )
        notificationTriggered = notifResponse.ok
      } catch {
        // Notification failure should not roll back the post
        notificationTriggered = false
      }
    }

    // Write audit log entry
    await writeAuditEntry({
      school_id: schoolId,
      absence_id: absenceId,
      action: 'plan_posted',
      target_date: date,
      details: {
        posted_count: postedAssignments.length,
        assignment_ids: postedAssignments.map((a) => a.id),
      },
    })

    return {
      posted_count: postedAssignments.length,
      notification_triggered: notificationTriggered,
    }
  })
}

// ============================================================================
// recallAndRedraft
// ============================================================================

/**
 * Revert all posted assignments for a given absence+date back to draft status.
 * Sends cancellation notifications to all previously-notified substitutes.
 * Writes a `plan_recalled` audit log entry.
 * Requirements: 6.7, 6.8, 6.9
 */
export async function recallAndRedraft(absenceId: string, date: string): Promise<void> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const headers = authHeaders(accessToken)

    // Fetch absence to get school_id
    const absenceParams = new URLSearchParams()
    absenceParams.append('select', 'school_id')
    absenceParams.append('id', `eq.${absenceId}`)

    const absenceResponse = await fetch(`${SUPABASE_URL}/rest/v1/absences?${absenceParams}`, {
      headers,
    })

    if (!absenceResponse.ok) {
      const err = await absenceResponse.json().catch(() => ({}))
      throw new Error(err.message || 'Failed to fetch absence')
    }

    const absences: any[] = await absenceResponse.json()
    if (!absences || absences.length === 0) throw new Error('Absence not found')
    const schoolId: string = absences[0].school_id

    // Sender email check removed: emails always go via the system Gmail account

    // Fetch posted assignments to get IDs for notifications
    const fetchParams = new URLSearchParams()
    fetchParams.append('select', 'id,substitute_teacher_id')
    fetchParams.append('absence_id', `eq.${absenceId}`)
    fetchParams.append('date', `eq.${date}`)
    fetchParams.append('status', `eq.posted`)

    const fetchResponse = await fetch(`${SUPABASE_URL}/rest/v1/assignments?${fetchParams}`, {
      headers,
    })

    if (!fetchResponse.ok) {
      const err = await fetchResponse.json().catch(() => ({}))
      throw new Error(err.message || 'Failed to fetch posted assignments')
    }

    const postedAssignments: any[] = await fetchResponse.json()

    if (postedAssignments.length === 0) {
      throw new ApiError('No posted assignments found for this date', 'NOT_FOUND', 404)
    }

    // Revert posted assignments to draft
    const patchParams = new URLSearchParams()
    patchParams.append('absence_id', `eq.${absenceId}`)
    patchParams.append('date', `eq.${date}`)
    patchParams.append('status', `eq.posted`)

    const patchResponse = await fetch(
      `${SUPABASE_URL}/rest/v1/assignments?${patchParams}`,
      {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ status: 'draft' }),
      }
    )

    if (!patchResponse.ok) {
      const err = await patchResponse.json().catch(() => ({}))
      throw new Error(err.message || 'Failed to revert assignments to draft')
    }

    // Send cancellation notifications for assignments that had a substitute
    const assignmentIdsWithSub = postedAssignments
      .filter((a) => a.substitute_teacher_id !== null)
      .map((a) => a.id)

    if (assignmentIdsWithSub.length > 0) {
      try {
        await fetch(`${SUPABASE_URL}/functions/v1/send-notification`, {
          method: 'POST',
          headers: {
            apikey: SUPABASE_ANON_KEY,
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            assignmentIds: assignmentIdsWithSub,
            type: 'cancellation',
          }),
        })
      } catch {
        // Notification failure should not block the recall
      }
    }

    // Write audit log entry
    await writeAuditEntry({
      school_id: schoolId,
      absence_id: absenceId,
      action: 'plan_recalled',
      target_date: date,
      details: {
        recalled_count: postedAssignments.length,
        assignment_ids: postedAssignments.map((a) => a.id),
      },
    })
  })
}


// ============================================================================
// createAssignment
// ============================================================================

/**
 * Create a single draft assignment record for manual assignment.
 * Used when an admin clicks "+ Assign" on a period that has no DB record yet
 * (i.e. the engine has never run for this period).
 * Writes an `assignment_manual_override` audit log entry on success.
 * Requirements: 5.1, 5.3
 */
export async function createAssignment(payload: {
  school_id: string
  absence_id: string
  date: string
  class_section_id: string
  period_id: string
  subject_id: string
  absent_teacher_id: string
  substitute_teacher_id: string
}): Promise<DraftAssignment> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const headers = authHeaders(accessToken)

    const response = await fetch(`${SUPABASE_URL}/rest/v1/assignments`, {
      method: 'POST',
      headers: {
        ...headers,
        Prefer: 'return=representation',
      },
      body: JSON.stringify({
        ...payload,
        status: 'draft',
        assignment_method: 'manual',
      }),
    })

    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      throw new Error(err.message || 'Failed to create assignment')
    }

    const rows: any[] = await response.json()
    if (!rows || rows.length === 0) throw new Error('Assignment not created')
    const created = rows[0]

    // Write audit log entry
    await writeAuditEntry({
      school_id: payload.school_id,
      absence_id: payload.absence_id,
      assignment_id: created.id,
      action: 'assignment_manual_override',
      target_date: payload.date,
      details: {
        old_substitute_teacher_id: null,
        new_substitute_teacher_id: payload.substitute_teacher_id,
      },
    })

    return created as DraftAssignment
  })
}

// ============================================================================
// getPostedAssignmentsForDate
// ============================================================================

/**
 * Fetch all posted assignments for a specific date and school.
 * Used by the timetable overlay to show substitutions on a selected date.
 * Only returns posted assignments — draft assignments are never shown in the timetable view.
 * Requirements: 17.1, 17.4, 17.5
 */
export async function getPostedAssignmentsForDate(
  schoolId: string,
  date: string
): Promise<DraftAssignment[]> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const headers = authHeaders(accessToken)

    const params = new URLSearchParams()
    params.append(
      'select',
      [
        '*',
        'absence:absences!inner(school_id)',
        'class_section:class_sections(*,class:classes(*,class_schedule:class_schedules(*)))',
        'period:schedule_periods(*)',
        'subject:subjects(*)',
        'substitute_teacher:teachers!assignments_substitute_teacher_id_fkey(*)',
        'absent_teacher:teachers!assignments_absent_teacher_id_fkey(*)',
      ].join(',')
    )
    params.append('date', `eq.${date}`)
    params.append('status', `eq.posted`)
    params.append('absence.school_id', `eq.${schoolId}`)
    params.append('order', 'period_id.asc')

    const response = await fetch(`${SUPABASE_URL}/rest/v1/assignments?${params}`, { headers })

    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      throw new Error(err.message || 'Failed to fetch posted assignments')
    }

    const assignments: any[] = await response.json()

    return assignments as DraftAssignment[]
  })
}
